// @ts-nocheck - Supabase type definitions need updating
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, AlertCircle, Check, Instagram, Copy } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { motion, AnimatePresence } from 'framer-motion';
import { isSupabaseStorageUrl } from '../utils/video/player'; 
import { generateThumbnailFromUrl } from '../utils/video/generateThumbnailFromUrl';
import { useStore } from '../store';
import { supabase } from '../lib/supabase';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { fetchInstagramThumbnail } from '../utils/fetchInstagramThumbnail';
import { scanInstagramAudio } from '../utils/scanInstagramAudio';

interface VideoPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  videoName?: string;
  platform: string;
  creativeId?: string;
}

interface ChatMessage {
  id: string;
  type: 'system' | 'user';
  content: string;
  timestamp: Date;
  platform?: 'instagram' | 'tiktok';
  isNew?: boolean; // Indicates if this URL was added after the last activation
}

export const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  isOpen,
  onClose,
  videoUrl,
  videoName,
  platform,
  creativeId
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copyingMessage, setCopyingMessage] = useState<string | null>(null);
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null);
  const [instagramThumbnailUrl, setInstagramThumbnailUrl] = useState<string | null>(null);
  // TikTok Spark Ad preview — populated by fetch-tiktok-spark-ad-info when
  // the user opens the modal for a TikTok creative (if not already cached).
  const [tiktokPreviewUrl, setTiktokPreviewUrl] = useState<string | null>(null);
  const [tiktokPosterUrl, setTiktokPosterUrl] = useState<string | null>(null);
  const [isReloadingTikTok, setIsReloadingTikTok] = useState(false);
  const [isReauthenticating, setIsReauthenticating] = useState(false);
  const tiktokVideoRef = useRef<HTMLVideoElement>(null);
  // Instagram reel video file (mirrored to Supabase Storage for playback)
  const [igVideoUrl, setIgVideoUrl] = useState<string | null>(null);
  const [isFetchingIgVideo, setIsFetchingIgVideo] = useState(false);
  // Generic video thumbnail (ad_creatives.thumbnail_url). Used as the
  // <video poster> so the player doesn't flash black before first frame
  // paints, especially on autoplay-blocked browsers.
  const [videoPosterUrl, setVideoPosterUrl] = useState<string | null>(null);
  const [artistId, setArtistId] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const { fetchAdCreatives, adCreatives, updateAdCreativeStatus } = useStore();
  const { client: artistClient, isProtected } = useArtistClient();
  
  // Fetch existing data for this creative
  useEffect(() => {
    if (isOpen && creativeId) {
      const fetchCreativeData = async () => {
        try {
          // Fetch creative data including thumbnail and timestamp data
          const { data, error } = await supabase.from('ad_creatives')
            .select('content, platform, merged_instagram_reel_url, merged_tiktok_auth_code, thumbnail_url, instagram_thumbnail_url, tiktok_thumbnail_url, tiktok_item_id, tiktok_post_fetch_error, tiktok_post_preview_url, tiktok_post_poster_url, tiktok_post_poster_stored_url, tiktok_post_video_stored_url, ig_video_stored_url, instagram_url_updated_at, tiktok_url_updated_at, last_activated_at, status, created_at, artists_id')
            .eq('id', creativeId).single();

          if (error) throw error;
          
          console.log('VideoPreviewModal: Fetched creative data:', {
            creativeId,
            platform: data.platform,
            content: data.content,
            hasInstagram: !!data.merged_instagram_reel_url,
            hasTikTok: !!data.merged_tiktok_auth_code,
            instagramUrl: data.merged_instagram_reel_url,
            tiktokCode: data.merged_tiktok_auth_code,
            instagramThumbnailUrl: data.instagram_thumbnail_url,
            videoUrlProp: videoUrl
          });

          // Check if content field contains TikTok auth code
          const hasTikTokInContent = data.platform === 'tiktok' && data.content?.startsWith('#');
          console.log('Has TikTok auth code in content field:', hasTikTokInContent);

          // Store Instagram thumbnail URL
          if (data.instagram_thumbnail_url) {
            setInstagramThumbnailUrl(data.instagram_thumbnail_url);
          }

          // Instagram reel video: use cached stored URL, or fetch from
          // RapidAPI + mirror to Supabase Storage for playback.
          if (data.ig_video_stored_url) {
            setIgVideoUrl(data.ig_video_stored_url);
          } else if (data.platform === 'instagram' && data.content?.includes('instagram.com')) {
            const igUrl = data.merged_instagram_reel_url || data.content;
            setIsFetchingIgVideo(true);
            (async () => {
              try {
                const res = await fetch(
                  'https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/fetch-ig-video',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${(window as any).supabase?.auth?.session()?.access_token || ''}`,
                    },
                    body: JSON.stringify({ creative_id: creativeId, instagram_url: igUrl }),
                  }
                );
                const body = await res.json();
                if (body?.video_url) {
                  setIgVideoUrl(body.video_url);
                  // Patch store so list view picks it up
                  useStore.setState((state: any) => ({
                    adCreatives: state.adCreatives.map((x: any) =>
                      String(x.id) === String(creativeId)
                        ? { ...x, ig_video_stored_url: body.video_url }
                        : x
                    ),
                  }));
                }
              } catch (err) {
                console.error('[ig-video] fetch failed:', err);
              } finally {
                setIsFetchingIgVideo(false);
              }
            })();
          }

          // Store artist ID for the direct link
          if (data.artists_id) {
            setArtistId(data.artists_id);
          }

          // Pick the best video poster for the <video poster> attribute —
          // prefer the stored thumbnail (permanent Supabase URL), then
          // Instagram/TikTok fallbacks. Shown before the first frame
          // paints (avoids black flash while the video buffers).
          const bestPoster =
            data.thumbnail_url ||
            data.instagram_thumbnail_url ||
            data.tiktok_post_poster_stored_url ||
            data.tiktok_post_poster_url ||
            null;
          if (bestPoster) setVideoPosterUrl(bestPoster);

          // Pre-populate TikTok Spark Ad video/poster URLs from DB cache so
          // the player renders immediately on open if we already resolved
          // them before. Prefer the permanently-mirrored URLs over TikTok
          // CDN URLs (which expire within hours).
          const cachedPreview =
            data.tiktok_post_video_stored_url || data.tiktok_post_preview_url || null;
          const cachedPoster =
            data.tiktok_post_poster_stored_url ||
            data.tiktok_post_poster_url ||
            data.tiktok_thumbnail_url ||
            null;
          if (cachedPreview) setTiktokPreviewUrl(cachedPreview);
          if (cachedPoster) setTiktokPosterUrl(cachedPoster);

          // TikTok: if we have an auth code (either in
          // merged_tiktok_auth_code OR inline in content starting with '#'),
          // and no tiktok_item_id yet, trigger fetch-tiktok-spark-ad-info on
          // the reporting project to authorize + fetch the Spark Ad post info
          // (item_id, poster_url, preview_url, duration, etc.) and persist it
          // to ad_creatives. Fire-and-forget.
          const tiktokAuthCodePresent =
            !!data.merged_tiktok_auth_code ||
            (data.platform === 'tiktok' &&
              typeof data.content === 'string' &&
              data.content.startsWith('#'));
          // Trigger when we don't have an item_id yet OR when item_id exists
          // but the preview/video URLs are missing (CDN URLs expire ~1h, so a
          // creative may have been resolved before but lost its playable URL).
          const tiktokNeedsResolve =
            !data.tiktok_item_id ||
            !(data.tiktok_post_video_stored_url || data.tiktok_post_preview_url);
          if (
            data.platform === 'tiktok' &&
            tiktokAuthCodePresent &&
            tiktokNeedsResolve
          ) {
            const REPORTING_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
            (async () => {
              try {
                const { data: { session } } = await supabase.auth.getSession();
                const token = session?.access_token;
                if (!token) {
                  console.warn('[tiktok-spark] no session — skipping auto fetch');
                  return;
                }
                const res = await fetch(
                  `${REPORTING_URL}/functions/v1/fetch-tiktok-spark-ad-info`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ creative_id: creativeId }),
                  },
                );
                const body = await res.json();
                if (body?.success) {
                  console.log('[tiktok-spark] fetched', body);
                  // Update local state so the video player picks up the
                  // freshly-resolved preview_url without needing a full
                  // ad_creatives refetch.
                  if (body.preview_url) {
                    setTiktokPreviewUrl(body.preview_url);
                  }
                  if (body.poster_url) {
                    setTiktokPosterUrl(body.poster_url);
                  }
                  // Optimistically patch the single creative in the store
                  // so the list-view thumbnail column picks up the fresh
                  // poster without waiting for the background refetch.
                  useStore.setState((state: any) => ({
                    adCreatives: state.adCreatives.map((x: any) =>
                      String(x.id) === String(creativeId)
                        ? {
                            ...x,
                            tiktok_post_poster_url:
                              body.poster_url ?? x.tiktok_post_poster_url,
                            tiktok_post_preview_url:
                              body.preview_url ?? x.tiktok_post_preview_url,
                            tiktok_thumbnail_url:
                              x.tiktok_thumbnail_url ?? body.poster_url ?? null,
                          }
                        : x,
                    ),
                  }));
                  // Background refresh with the remembered filters and
                  // skipCache=true so list reads fresh DB values (not the
                  // pre-spark-ad cached payload).
                  const lastFilters = (useStore.getState() as any)
                    ._adCreativesLastFilters;
                  fetchAdCreatives(undefined, undefined, lastFilters, true);
                  // Fire-and-forget: permanently mirror the Spark Ad poster
                  // (and video) into our thumbnails/videos buckets. The
                  // tt_video/info preview/poster URLs only live ~1h before
                  // TikTok's CDN 403s. download-tiktok-spark-ad-media writes
                  // tiktok_post_poster_stored_url + tiktok_post_video_stored_url
                  // which are used for long-lived display.
                  try {
                    fetch(
                      `${REPORTING_URL}/functions/v1/download-tiktok-spark-ad-media`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ creative_id: creativeId }),
                      },
                    )
                      .then((r) => r.json())
                      .then((dl) => {
                        console.log('[tiktok-spark] media mirrored', dl);
                        // Patch stored URLs optimistically so the list
                        // swaps to permanent URLs instead of the
                        // about-to-expire TikTok CDN links.
                        const r0 = dl?.results?.[0];
                        if (r0?.poster_stored_url || r0?.video_stored_url) {
                          useStore.setState((state: any) => ({
                            adCreatives: state.adCreatives.map((x: any) =>
                              String(x.id) === String(creativeId)
                                ? {
                                    ...x,
                                    tiktok_post_poster_stored_url:
                                      r0.poster_stored_url ??
                                      x.tiktok_post_poster_stored_url,
                                    tiktok_post_video_stored_url:
                                      r0.video_stored_url ??
                                      x.tiktok_post_video_stored_url,
                                    tiktok_thumbnail_url:
                                      r0.poster_stored_url ??
                                      x.tiktok_thumbnail_url,
                                  }
                                : x,
                            ),
                          }));
                        }
                        const nextFilters = (useStore.getState() as any)
                          ._adCreativesLastFilters;
                        fetchAdCreatives(undefined, undefined, nextFilters, true);
                      })
                      .catch((err) =>
                        console.warn('[tiktok-spark] media mirror failed', err),
                      );
                  } catch (err) {
                    console.warn('[tiktok-spark] media mirror kickoff failed', err);
                  }
                } else {
                  console.warn('[tiktok-spark] fetch failed', body);
                }
              } catch (err) {
                console.error('[tiktok-spark] fetch error', err);
              }
            })();
          }
          
          // If this is an Instagram URL and we don't have a thumbnail yet, try to fetch one
          if (platform === 'instagram' && !data.instagram_thumbnail_url && videoUrl.includes('instagram.com')) {
            try {
              console.log('Fetching Instagram thumbnail for URL in preview modal:', videoUrl);
              // Pass the creative ID so the Edge Function can update the database directly
              fetchInstagramThumbnail(videoUrl, creativeId)
                .then((thumbnailUrl) => {
                  // Patch the single row in the store so the list view's
                  // thumbnail column updates live, without waiting for a
                  // cache-busting refetch.
                  if (thumbnailUrl) {
                    useStore.setState((state: any) => ({
                      adCreatives: state.adCreatives.map((x: any) =>
                        String(x.id) === String(creativeId)
                          ? { ...x, instagram_thumbnail_url: thumbnailUrl }
                          : x,
                      ),
                    }));
                  }
                  // Force a cache-skipping refresh so the next list read
                  // reflects the newly-stored thumbnail (and any other
                  // server-side side effects).
                  const filters = (useStore.getState() as any)
                    ._adCreativesLastFilters;
                  fetchAdCreatives(undefined, undefined, filters, true);
                })
                .catch(err => console.error('Error fetching Instagram thumbnail:', err));
            } catch (thumbnailError) {
              console.error('Error fetching Instagram thumbnail:', thumbnailError);
            }
          }
          
          // If no thumbnail exists and this is a video URL, generate one
          if (!data.thumbnail_url && platform === 'direct_upload' && isSupabaseStorageUrl(videoUrl)) {
            try {
              setGeneratingThumbnail(true);
              console.log('Generating thumbnail for video in preview modal:', videoUrl, 'Platform:', platform);
              
              generateThumbnailFromUrl(videoUrl)
                .then(thumbnailUrl => {
                  if (thumbnailUrl) {
                    // Update the ad creative with the new thumbnail
                    supabase.from('ad_creatives')
                      .update({ 
                        thumbnail_url: thumbnailUrl,
                        updated_at: new Date().toISOString()
                      })
                      .eq('id', creativeId)
                      .then(({ error: updateError }) => {
                        if (updateError) {
                          console.error('Error updating thumbnail:', updateError);
                        } else {
                          console.log('Thumbnail generated and saved successfully');
                          // Update the single creative in the store without refetching
                          // (refetching without filters would wipe the artist-filtered list)
                          useStore.setState((state: any) => ({
                            adCreatives: state.adCreatives.map((c: any) =>
                              c.id === creativeId
                                ? { ...c, thumbnail_url: thumbnailUrl }
                                : c
                            )
                          }));
                        }
                        setGeneratingThumbnail(false);
                      });
                  } else {
                    console.log('No thumbnail generated - null result');
                    setGeneratingThumbnail(false);
                  }
                })
                .catch(err => {
                  console.error('Error generating thumbnail in preview modal:', err);
                  setGeneratingThumbnail(false);
                });
            } catch (thumbnailError) {
              console.error('Error generating thumbnail in preview modal:', thumbnailError);
              setGeneratingThumbnail(false);
            }
          } else if (!data.thumbnail_url && platform === 'dropbox') {
            // Dropbox thumbnails cannot be generated due to CORS restrictions
            // The canvas becomes "tainted" and cannot be exported to data URL
            console.log('Dropbox thumbnail generation not supported due to CORS restrictions');
          }
          
          const initialMessages = [
            {
              id: '1',
              type: 'system' as const,
              content: 'You can submit your TikTok auth code (starting with #) or Instagram reel URL here. The system will automatically detect which type you\'re submitting.',
              timestamp: new Date()
            }
          ];
          
          // Add TikTok auth code from content field if it's a TikTok platform and content starts with #
          if (data.platform === 'tiktok' && data.content && data.content.startsWith('#')) {
            initialMessages.push({
              id: 'tiktok-content-' + Date.now(),
              type: 'system' as const,
              content: data.content,
              timestamp: new Date(data.created_at),
              platform: 'tiktok' as const,
              isNew: false
            });
          }
          
          // Add Instagram URL from content field if it's an Instagram platform
          if (data.platform === 'instagram' && data.content && data.content.includes('instagram.com')) {
            initialMessages.push({
              id: 'instagram-content-' + Date.now(),
              type: 'system' as const,
              content: data.content,
              timestamp: new Date(data.created_at),
              platform: 'instagram' as const,
              isNew: false
            });
          }
          
          // Add existing submissions to chat history (merged fields)
          if (data.merged_instagram_reel_url) {
            // Filter out any existing Instagram messages first
            const existingInstagramMessage = initialMessages.find(m => m.platform === 'instagram');
            if (!existingInstagramMessage) {
              // Check if this Instagram URL is new (added after last activation)
              const isNewInstagram = data.instagram_url_updated_at && data.last_activated_at 
                ? new Date(data.instagram_url_updated_at) > new Date(data.last_activated_at)
                : false;
              
              initialMessages.push({
                id: 'instagram-' + Date.now(),
                type: 'user' as const,
                content: data.merged_instagram_reel_url,
                timestamp: new Date(Date.now() - 60000), // 1 minute ago 
                platform: 'instagram' as const,
                isNew: isNewInstagram
              });
              
              // If we don't have an Instagram thumbnail yet, try to fetch it
              if (!data.instagram_thumbnail_url && data.merged_instagram_reel_url) {
                try {
                  // Pass the creative ID so the Edge Function can update the database directly
                  fetchInstagramThumbnail(data.merged_instagram_reel_url, creativeId)
                    .then((thumbnailUrl) => {
                      if (thumbnailUrl) {
                        useStore.setState((state: any) => ({
                          adCreatives: state.adCreatives.map((x: any) =>
                            String(x.id) === String(creativeId)
                              ? { ...x, instagram_thumbnail_url: thumbnailUrl }
                              : x,
                          ),
                        }));
                      }
                      const filters = (useStore.getState() as any)
                        ._adCreativesLastFilters;
                      fetchAdCreatives(undefined, undefined, filters, true);
                    })
                    .catch(err => console.error('Error fetching Instagram thumbnail:', err));
                } catch (thumbnailError) {
                  console.error('Error fetching Instagram thumbnail:', thumbnailError);
                }
              }
            }
          }
          
          if (data.merged_tiktok_auth_code) {
            // Filter out any existing TikTok messages first
            const existingTikTokMessage = initialMessages.find(m => m.platform === 'tiktok');
            if (!existingTikTokMessage) {
              // Check if this TikTok code is new (added after last activation)
              const isNewTikTok = data.tiktok_url_updated_at && data.last_activated_at 
                ? new Date(data.tiktok_url_updated_at) > new Date(data.last_activated_at)
                : false;
              
              initialMessages.push({
                id: 'tiktok-' + Date.now(),
                type: 'user' as const,
                content: data.merged_tiktok_auth_code,
                timestamp: new Date(Date.now() - 120000), // 2 minutes ago
                platform: 'tiktok' as const,
                isNew: isNewTikTok
              });
            }
          }
          
          setMessages(initialMessages);
        } catch (error) {
          console.error('Error fetching creative data:', error);
          setMessages([
            {
              id: 'system-' + Date.now(),
              type: 'system' as const,
              content: 'You can submit your TikTok auth code (starting with #) or Instagram reel URL here. The system will automatically detect which type you\'re submitting.',
              timestamp: new Date()
            }
          ]);
        }
      };
      
      fetchCreativeData();
    }
  }, [isOpen, creativeId, videoUrl, platform, fetchAdCreatives]);

  // Initialize chat with system message
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setError(null);
      setSuccess(null);
      setInstagramThumbnailUrl(null);
      setTiktokPreviewUrl(null);
      setTiktokPosterUrl(null);
      setVideoPosterUrl(null);
      setIgVideoUrl(null);
      setIsFetchingIgVideo(false);
    }
  }, [isOpen, creativeId]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const detectInputType = (input: string): 'instagram' | 'tiktok' | 'unknown' => {
    const trimmedInput = input.trim();
    
    if (trimmedInput.startsWith('#')) {
      return 'tiktok';
    }
    
    if (trimmedInput.includes('instagram.com') && 
        (trimmedInput.includes('/reel/') || trimmedInput.includes('/reels/') || trimmedInput.includes('/p/'))) {
      return 'instagram';
    }
    
    return 'unknown';
  };

  const validateInput = (input: string, type: 'instagram' | 'tiktok'): boolean => {
    if (type === 'tiktok') {
      return input.startsWith('#') && input.length > 1;
    }
    
    if (type === 'instagram') {
      try {
        const url = new URL(input);
        return url.hostname.includes('instagram.com') && 
               (url.pathname.includes('/reel/') || url.pathname.includes('/reels/') || url.pathname.includes('/p/'));
      } catch {
        return false;
      }
    }
    
    return false;
  };

  const handleReloadTikTokMedia = async (isFromError = false) => {
    if (!creativeId) return;

    setIsReloadingTikTok(true);
    if (isFromError) {
      setIsReauthenticating(true);
    }
    setError(null);
    setSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError('No active session');
        setIsReloadingTikTok(false);
        return;
      }

      const REPORTING_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
      const res = await fetch(
        `${REPORTING_URL}/functions/v1/download-tiktok-spark-ad-media`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ creative_id: creativeId }),
        },
      );

      const body = await res.json();

      if (!res.ok) {
        throw new Error(body.error || 'Failed to reload TikTok media');
      }

      // Check individual result status — the edge function returns HTTP 200
      // even when individual creatives fail (e.g. expired auth code)
      const result = body.results?.[0]
      if (result && (result.status === 'refresh_failed' || result.status === 'error')) {
        const errMsg = result.error || 'Failed to reload TikTok media'
        const isExpired = errMsg.includes('Post code is incorrect') || errMsg.includes('authorization code has expired')
        throw new Error(isExpired ? 'TikTok auth code has expired — ask the creator for a new code' : errMsg)
      }

      setSuccess('TikTok media reloaded successfully');

      // Refresh the creative data to get updated URLs
      if (creativeId) {
        const { data: updatedData } = await supabase
          .from('ad_creatives')
          .select('*')
          .eq('id', creativeId)
          .single();

        if (updatedData) {
          const cachedPreview =
            updatedData.tiktok_post_video_stored_url || updatedData.tiktok_post_preview_url || null;
          const cachedPoster =
            updatedData.tiktok_post_poster_stored_url ||
            updatedData.tiktok_post_poster_url ||
            updatedData.tiktok_thumbnail_url ||
            null;
          if (cachedPreview) setTiktokPreviewUrl(cachedPreview);
          if (cachedPoster) setTiktokPosterUrl(cachedPoster);

          // Update store
          useStore.setState((state: any) => ({
            adCreatives: state.adCreatives.map((x: any) =>
              String(x.id) === String(creativeId) ? { ...x, ...updatedData } : x
            )
          }));
        }
      }
    } catch (err: any) {
      console.error('Error reloading TikTok media:', err);
      setError(err.message || 'Failed to reload TikTok media');
    } finally {
      setIsReloadingTikTok(false);
      setIsReauthenticating(false);
    }
  };

  // Auto-play TikTok video after successful reload
  useEffect(() => {
    if (tiktokPreviewUrl && tiktokVideoRef.current && platform === 'tiktok') {
      console.log('Auto-playing TikTok video after reload');
      tiktokVideoRef.current.play().catch(err => {
        console.log('Auto-play failed (browser policy):', err);
      });
    }
  }, [tiktokPreviewUrl, platform]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputValue.trim() || !creativeId) return;
    
    const trimmedInput = inputValue.trim();
    const inputType = detectInputType(trimmedInput);
    
    if (inputType === 'unknown') {
      setError('Please enter a valid TikTok auth code (starting with #) or Instagram reel URL');
      return;
    }
    
    if (!validateInput(trimmedInput, inputType)) {
      setError(inputType === 'tiktok' 
        ? 'TikTok auth code must start with # and contain additional characters'
        : 'Please enter a valid Instagram reel URL'
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Add user message to chat
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: trimmedInput,
        timestamp: new Date(),
        platform: inputType
      };
      
      setMessages(prev => [...prev, userMessage]);

      // Update the ad creative in the database
      const updateData: any = {
        status: 'pending', // Set status back to pending when artist adds new content
        tiktok_url_updated_at: new Date().toISOString()
      };
      if (inputType === 'tiktok') {
        // Check if we're replacing an existing code
        const existingTikTokCode = messages.find(m => m.platform === 'tiktok' && m.type === 'user');
        let isReplacement = false;
        let previousContent = '';
       
        if (existingTikTokCode) {
          isReplacement = true;
          previousContent = existingTikTokCode.content;
          // Add replacement message to chat
          const replacementMessage: ChatMessage = {
            id: (Date.now() + 3).toString(),
            type: 'system',
            content: `Replacing previous TikTok auth code: ${existingTikTokCode.content}`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, replacementMessage]);
        }
        updateData.merged_tiktok_auth_code = trimmedInput; 
       
       // Notify team about the replacement via WhatsApp
       if (isReplacement && previousContent) {
         try {
           // Get artist info
           const { data: creativeData, error: creativeError } = await supabase
             .from('ad_creatives')
             .select('artists_id, artists!inner(name, whatsapp_group_id)')
             .eq('id', creativeId)
             .single();
           
           if (!creativeError && creativeData) {
             const { artists } = creativeData;
             
             // Send notification asynchronously
             const { WhatsAppService } = await import('../services/whatsapp');
             WhatsAppService.notifyAdCreativeUpdate({
               artistName: artists.name,
               artistId: creativeData.artists_id,
               artistGroupId: artists.whatsapp_group_id,
               platform: 'tiktok',
               content: trimmedInput,
               videoName: videoName,
               status: 'pending',
               isReplacement: true,
               previousContent
             }).catch(console.error);
           }
         } catch (notifyError) {
           console.error('Error sending replacement notification:', notifyError);
         }
       }
      } else {
        // Check if we're replacing an existing URL
        const existingInstagramUrl = messages.find(m => m.platform === 'instagram' && m.type === 'user');
        let isReplacement = false; 
        let previousContent = ''; 
       
        if (existingInstagramUrl) {
          isReplacement = true;
          previousContent = existingInstagramUrl.content;
          // Add replacement message to chat
          const replacementMessage: ChatMessage = {
            id: (Date.now() + 3).toString(),
            type: 'system',
            content: `Replacing previous Instagram URL: ${existingInstagramUrl.content}`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, replacementMessage]);
        }
        updateData.merged_instagram_reel_url = trimmedInput;
        updateData.instagram_url_updated_at = new Date().toISOString();
        updateData.status = 'pending'; // Set status back to pending when artist adds new content
       
        // Try to fetch Instagram thumbnail
        try {
          console.log('Fetching Instagram thumbnail for new URL:', trimmedInput);
          // Pass the creative ID so the Edge Function can update the database directly
          fetchInstagramThumbnail(trimmedInput, creativeId)
            .then((thumbnailUrl) => {
              if (thumbnailUrl) {
                useStore.setState((state: any) => ({
                  adCreatives: state.adCreatives.map((x: any) =>
                    String(x.id) === String(creativeId)
                      ? { ...x, instagram_thumbnail_url: thumbnailUrl }
                      : x,
                  ),
                }));
              }
              const filters = (useStore.getState() as any)
                ._adCreativesLastFilters;
              fetchAdCreatives(undefined, undefined, filters, true);
            })
            .catch(err => console.error('Error fetching Instagram thumbnail:', err));
        } catch (thumbnailError) {
          console.error('Error fetching Instagram thumbnail:', thumbnailError);
        }

        // Scan Instagram audio type. If licensed_music, backend downloads the
        // video and replaces merged_instagram_reel_url/content with a storage URL.
        scanInstagramAudio(creativeId, trimmedInput)
          .then((result) => {
            if (!result) return;
            if (result.licensed && result.downloaded) {
              const msg: ChatMessage = {
                id: (Date.now() + 10).toString(),
                type: 'system',
                content:
                  'Licensed audio detected — the reel is still promoted as the real Instagram post (regular ad).',
                timestamp: new Date(),
              };
              setMessages((prev) => [...prev, msg]);
            } else if (result.audio_type === 'original_sounds') {
              const msg: ChatMessage = {
                id: (Date.now() + 10).toString(),
                type: 'system',
                content: '✅ Original sound detected — reel is eligible for advertising.',
                timestamp: new Date(),
              };
              setMessages((prev) => [...prev, msg]);
            } else if (result.error) {
              console.error('Audio scan error:', result.error);
            }
            fetchAdCreatives().catch(console.error);
          })
          .catch((err) => console.error('Audio scan error:', err));
        
       // Notify team about the replacement via WhatsApp
       if (isReplacement && previousContent) {
         try {
           // Get artist info
           const { data: creativeData, error: creativeError } = await supabase
             .from('ad_creatives')
             .select('artists_id, artists!inner(name, whatsapp_group_id)')
             .eq('id', creativeId)
             .single();
           
           if (!creativeError && creativeData) {
             const { artists } = creativeData;
             
             // Send notification asynchronously
             const { WhatsAppService } = await import('../services/whatsapp');
             WhatsAppService.notifyAdCreativeUpdate({
               artistName: artists.name,
               artistId: creativeData.artists_id,
               artistGroupId: artists.whatsapp_group_id,
               platform: 'instagram',
               content: trimmedInput,
               videoName: videoName,
               status: 'pending',
               isReplacement: true,
               previousContent
             }).catch(console.error);
           }
         } catch (notifyError) {
           console.error('Error sending replacement notification:', notifyError);
         }
       }
      }

      const { error: updateError } = await supabase
        .from('ad_creatives')
        .update(updateData)
        .eq('id', creativeId)
        .select();

      if (updateError) throw updateError;

      // Add success message to chat
      const successMessage: ChatMessage = {
        id: (Date.now() + 4).toString(),
        type: 'system',
        content: `✅ ${inputType === 'tiktok' ? 'TikTok auth code' : 'Instagram reel URL'} saved successfully!`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, successMessage]);
      setSuccess(`${inputType === 'tiktok' ? 'TikTok auth code' : 'Instagram reel URL'} saved successfully!`);
      setInputValue('');
      
      // Refresh ad creatives data
      fetchAdCreatives().catch(console.error);
      
      // Update the messages list to show only the latest submission for each platform
      setMessages(prev => {
        // Keep system messages and the latest user message for each platform
        const systemMessages = prev.filter(m => m.type === 'system');
        
        // For user messages, keep only the latest for each platform
        const latestTikTok = prev
          .filter(m => m.type === 'user' && m.platform === 'tiktok')
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
          
        const latestInstagram = prev
          .filter(m => m.type === 'user' && m.platform === 'instagram')
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
        
        const userMessages = [latestTikTok, latestInstagram].filter(Boolean);
        
        return [...systemMessages, ...userMessages];
      });
      
    } catch (err) {
      console.error('Error updating ad creative:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save';
      setError(errorMessage);
      
      // Add error message to chat
      const errorChatMessage: ChatMessage = {
        id: (Date.now() + 5).toString(),
        type: 'system',
        content: `❌ Error: ${errorMessage}`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorChatMessage]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const handleDeleteMessage = async (message: ChatMessage) => {
    if (!creativeId || !message.platform) return;
    
    try {
      // Update the database to remove the URL/code
      const updateData: any = {};
      
      if (message.platform === 'instagram') {
        updateData.merged_instagram_reel_url = null;
        updateData.instagram_url_updated_at = null;
      } else if (message.platform === 'tiktok') {
        updateData.merged_tiktok_auth_code = null;
        updateData.tiktok_url_updated_at = null;
      }
      
      const { error: updateError } = await supabase
        .from('ad_creatives')
        .update(updateData)
        .eq('id', creativeId);
      
      if (updateError) throw updateError;
      
      // Remove the message from the chat
      setMessages(prev => prev.filter(m => m.id !== message.id));
      
      // Refresh ad creatives data
      await fetchAdCreatives();
      
      setSuccess(`${message.platform === 'instagram' ? 'Instagram URL' : 'TikTok code'} deleted successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleCopyAndActivate = async (message: ChatMessage) => {
    if (!creativeId) return;
    
    try {
      setCopyingMessage(message.id);
      
      // Extract content to copy
      let contentToCopy = message.content;
      
      // For Instagram URLs, extract only the short code
      if (message.platform === 'instagram' && message.content.includes('instagram.com')) {
        try {
          const url = new URL(message.content);
          const pathParts = url.pathname.split('/').filter(Boolean);
          // Instagram URLs are typically: /reel/CODE or /p/CODE
          const codeIndex = pathParts.findIndex(part => part === 'reel' || part === 'p');
          if (codeIndex !== -1 && pathParts[codeIndex + 1]) {
            contentToCopy = pathParts[codeIndex + 1];
          }
        } catch (urlError) {
          console.error('Error parsing Instagram URL:', urlError);
          // Fall back to original content if parsing fails
        }
      }
      
      // Copy to clipboard
      await navigator.clipboard.writeText(contentToCopy);
      
      // Set creative to active. The store patches the row in place, so we
      // intentionally do NOT call fetchAdCreatives() here — a re-fetch
      // would re-sort the list and cause a visible jump/flicker, plus
      // potentially serve stale cached rows that revert the new status.
      await updateAdCreativeStatus(creativeId, 'active', null, isProtected ? artistClient : undefined);

      setSuccess(`Copied ${message.platform === 'instagram' ? 'short code' : 'content'} and set to active!`);
      
      // Clear the "NEW" badge by updating the message
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, isNew: false } : m
      ));
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Error copying and activating:', err);
      setError('Failed to copy and activate');
      setTimeout(() => setError(null), 3000);
    } finally {
      setCopyingMessage(null);
    }
  };

  if (!isOpen) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center py-[10vh] px-0 sm:p-4 z-50"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-7xl h-[80vh] sm:w-[95vw] sm:h-[90vh] flex flex-col overflow-hidden"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="min-w-0 flex-1 pr-2">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                Video Preview
              </h2>
              {videoName && (
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                  {videoName}
                </p>
              )}
              {videoUrl && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 break-all hidden sm:block">
                  {videoUrl}
                </p>
              )}
              {creativeId && artistId && (
                <a
                  href={`https://tool.swipeup-marketing.com/artist/${artistId}/?video_id=${creativeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-1 block"
                >
                  Open in Artist View →
                </a>
              )}
            </div>
            <motion.button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-2 -mr-2 touch-manipulation"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Close"
            >
              <X className="h-6 w-6 sm:h-5 sm:w-5" />
            </motion.button>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
            {/* Video Section */}
            <div className="flex items-center justify-center p-3 sm:p-4 bg-black flex-shrink-0 w-full lg:w-1/2 h-[40vh] sm:h-[45vh] lg:h-auto min-h-0 overflow-hidden">
              {/* Organic Instagram reel: show poster thumbnail + play button
                   that opens the reel on instagram.com. For licensed-music
                   replaced reels the content has been mirrored to a Supabase
                   storage .mp4 — those fall through to the VideoPlayer
                   branch below so we play the stored video directly.
                   Note: we render this fallback whenever the URL is NOT a
                   Supabase storage video, even if the thumbnail fetch
                   failed — handing instagram.com/reel/... to <VideoPlayer>
                   would otherwise produce a "Format error" because the
                   browser tries to decode the HTML page as video. */}
              {platform === 'instagram' && !isSupabaseStorageUrl(videoUrl) ? (
                igVideoUrl ? (
                  <div className="h-full flex items-center justify-center">
                    <VideoPlayer
                      key={igVideoUrl}
                      url={igVideoUrl}
                      poster={videoPosterUrl || instagramThumbnailUrl || undefined}
                      autoPlay={true}
                    />
                  </div>
                ) : isFetchingIgVideo ? (
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <div className="w-8 h-8 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
                    <span className="text-sm">Loading video…</span>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <iframe
                      src={`${videoUrl.replace(/\/$/, '')}/embed/`}
                      className="rounded-lg"
                      style={{ width: '100%', height: '100%', maxHeight: '100%', border: 'none' }}
                      allowFullScreen
                      scrolling="no"
                      title="Instagram reel preview"
                    />
                  </div>
                )
              ) : (platform === 'dropbox' || platform === 'direct_upload' || (platform === 'instagram' && !videoUrl.startsWith('#') && videoUrl.includes('://'))) ? (
                <div className="h-full flex items-center justify-center">
                  <VideoPlayer
                    key={videoUrl}
                    url={videoUrl}
                    poster={videoPosterUrl || undefined}
                    forwardedRef={tiktokVideoRef}
                    autoPlay={true}
                  />
                </div>
              ) : platform === 'tiktok' && tiktokPreviewUrl && !isReauthenticating ? (
                // Spark Ad video resolved via fetch-tiktok-spark-ad-info.
                // preview_url is a direct .mp4 from TikTok CDN (expires ~1h
                // after fetch) OR our permanently-mirrored storage URL.
                <div className="h-full flex items-center justify-center">
                  <VideoPlayer
                    key={tiktokPreviewUrl}
                    url={tiktokPreviewUrl}
                    poster={tiktokPosterUrl || videoPosterUrl || undefined}
                    forwardedRef={tiktokVideoRef}
                    onError={() => {
                      console.log('TikTok video error - auto-reauthenticating');
                      handleReloadTikTokMedia(true);
                    }}
                    autoPlay={true}
                  />
                </div>
              ) : platform === 'tiktok' && isReauthenticating ? (
                // Show spinner while re-authenticating after video error
                <div className="h-full w-full flex flex-col items-center justify-center text-white text-center p-4">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mb-4"></div>
                  <h3 className="text-xl font-semibold mb-2">Re-authorizing TikTok</h3>
                  <p className="text-gray-300">
                    Fetching fresh media from TikTok…
                  </p>
                  <p className="text-gray-500 text-xs mt-2">
                    This usually takes a few seconds
                  </p>
                </div>
              ) : platform === 'tiktok' ? (
                <div className="h-full w-full flex flex-col items-center justify-center text-white text-center p-4">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">TikTok Spark Ad</h3>
                    <p className="text-gray-300">
                      {tiktokPreviewUrl ? 'Video loaded from cache' : 'Resolving preview via TikTok auth code…'}
                    </p>
                    {!tiktokPreviewUrl && (
                      <p className="text-gray-500 text-xs mt-2">
                        If this takes more than a few seconds, the auth code
                        may have expired — click the button below to reload.
                      </p>
                    )}
                    <button
                      onClick={() => handleReloadTikTokMedia(false)}
                      disabled={isReloadingTikTok}
                      className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isReloadingTikTok ? 'Reloading…' : 'Reload TikTok Media'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-white text-center">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">No preview available</h3>
                    <p className="text-gray-300">Use the chat section to submit your content.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Section */}
            {creativeId && (
              <div className="flex flex-col border-t lg:border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 w-full lg:w-1/2 flex-1 min-h-0 overflow-hidden">
                {/* Chat Header */}
                <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                    Submit Content
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Add your TikTok auth code or Instagram reel URL
                  </p>
                </div>

                {/* Chat Messages */}
                <div 
                  ref={chatRef}
                  className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4"
                >
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        className={`max-w-[85%] rounded-lg p-3 relative ${
                          message.type === 'user'
                            ? 'bg-primary-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        onMouseEnter={() => setHoveredMessage(message.id)}
                        onMouseLeave={() => setHoveredMessage(null)}
                      >
                        {message.isNew && message.type === 'user' && (
                          <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold shadow-lg flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <path d="M12 6v6l4 2"/>
                            </svg>
                            NEW
                          </div>
                        )}
                        {message.platform && hoveredMessage === message.id && (
                          <motion.button
                            onClick={() => handleDeleteMessage(message)}
                            className={`absolute -top-2 -right-2 rounded-full p-1 transition-colors ${
                              message.type === 'user'
                                ? 'bg-white/20 hover:bg-white/30 text-white'
                                : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <X className="h-3 w-3" />
                          </motion.button>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          {message.platform === 'instagram' && (
                            <Instagram className="h-4 w-4" />
                          )}
                          {message.platform === 'tiktok' && (
                            <svg 
                              viewBox="0 0 24 24" 
                              fill="currentColor"
                              className="h-4 w-4"
                            >
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                          )}
                          <span className="text-xs opacity-75">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </p>
                        
                        {/* Copy and Activate button for admin (for any message with platform) */}
                        {message.platform && (
                          <motion.button
                            onClick={() => handleCopyAndActivate(message)}
                            disabled={copyingMessage === message.id}
                            className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                              message.type === 'user' 
                                ? 'bg-white/20 hover:bg-white/30' 
                                : 'bg-primary-500 hover:bg-primary-600 text-white'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {copyingMessage === message.id ? (
                              <>
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                              </>
                            ) : (
                              <>
                                <Copy className="h-3 w-3" />
                                Copy & Set Active
                              </>
                            )}
                          </motion.button>
                        )}
                      </motion.div>
                    </motion.div>
                  ))}
                </div>

                {/* Status Messages */}
                <AnimatePresence>
                  {error && (
                    <motion.div 
                      className="mx-4 mb-2 flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-sm"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <p>{error}</p>
                    </motion.div>
                  )}
                  
                  {success && (
                    <motion.div 
                      className="mx-4 mb-2 flex items-center gap-2 text-green-500 bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-sm"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Check className="h-4 w-4 flex-shrink-0" />
                      <p>{success}</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Chat Input */}
                <form onSubmit={handleSubmit} className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        setError(null);
                        setSuccess(null);
                      }}
                      placeholder="Enter TikTok auth code (#...) or Instagram URL"
                      className="flex-1 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm"
                      disabled={isSubmitting}
                    />
                    <motion.button
                      type="submit"
                      disabled={!inputValue.trim() || isSubmitting}
                      className="px-3 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      whileHover={!isSubmitting && inputValue.trim() ? { scale: 1.05 } : {}}
                      whileTap={!isSubmitting && inputValue.trim() ? { scale: 0.95 } : {}}
                    >
                      <Send className="h-4 w-4" />
                    </motion.button>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <p>• TikTok auth codes start with #</p>
                    <p>• Instagram URLs should be reel or post links</p>
                    <p className="mt-1 italic">Note: New submissions will replace existing ones of the same type</p>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {generatingThumbnail ? (
                  <span className="text-sm text-primary-500 dark:text-primary-400 flex items-center gap-1">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating thumbnail...
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Platform:</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {platform}
                    </span>
                  </div>
                )}
              </div>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                Open in new tab
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};